<script>
    export let data;
    export let data_errors;
    export let scale;
    export let margin;
    export let unit;
    export let rec_limit;
    export let source;
    export let chemical_substance;
    export let scales;
    export let x_domain;
    export let selected_age;

    import HorizontalGraphPoints from './HorizontalGraphPoints.svelte';
    import Modal from './Modal.svelte';
    import { sortAgesBySection } from '../utils/utils';
    import { icon_folder } from '../inputs/data_paths';
    import constants from '../utils/constants';

    import { scaleBand, scaleLinear } from 'd3-scale';
    import _ from 'lodash';

    const axis_font_size = 12;

    // in this file the static axis is the y-axis and the dynamic axis is the x-axis
    let width = 0, height = 0;
    let prairie_data, canada_data;
    let x_scale, y_scale, y_domain;
    let lod, lod_path, lod_left, show_lod_num;
    let rec_limit_path, rec_limit_right;
    let min_age_label;
    $: {
        prairie_data = data["Prairies"];
        canada_data = data["Canada"];

        let total_data = sortAgesBySection(_.concat(prairie_data, canada_data), 'static').reverse();

        // total_data has sorted ages by max -> min so grab smallest Age
        min_age_label = _.get(
            _(total_data).filter((data_point) => data_point.section === 'Age').reverse().value(), '0.static', null);

        // get limit of detection
        lod = canada_data.length ? _.get(_.minBy(canada_data, 'lod'), 'lod', null) : null;
        show_lod_num = canada_data.length ? _.every(canada_data, (data_point) => data_point.lod === canada_data[0].lod) : null;

        x_scale = scaleLinear().domain(x_domain).range([margin.left, width - margin.right]);
        y_domain = _(total_data).map('static').uniq().value();
        let y_scale_band = scaleBand()
            .domain(y_domain)
            .range([height - margin.bottom, margin.top])
            .paddingInner(1)
            .paddingOuter(1);
        y_scale = (y_val) => {
            let shift;
            if (y_val === 'All') shift = -16;
            else if (y_val === 'Males' || y_val === 'Females') shift = 0;
            else shift = 16; // y_val is an age
            return y_scale_band(y_val) + shift;
        };

        // make sure lod width is at least *min_width*
        let min_width = 20;
        let lod_width = x_scale(lod) - margin.left;
        lod_left = (_.isNaN(lod) || _.isNil(lod) || lod < x_domain[0]
            ? null 
            : (lod_width < min_width ? margin.left - (min_width - lod_width) : margin.left));
        lod_path = (_.isNaN(lod) || _.isNil(lod) || lod < x_domain[0]
            ? null 
            : `M ${lod_left} ${margin.top} H ${x_scale(lod)} V ${height - margin.bottom} H ${lod_left}`
        );

        // make sure rec_limit width is at least *min_width*
        let rec_limit_width = width - margin.right - x_scale(rec_limit);
        rec_limit_right = (isNaN(rec_limit) || rec_limit > x_domain[1]
            ? null 
            : width - margin.right + (rec_limit_width < min_width ? min_width - rec_limit_width : 0));
        rec_limit_path = (isNaN(rec_limit) || rec_limit > x_domain[1]
            ? null 
            : `M ${rec_limit_right} ${margin.top} H ${x_scale(rec_limit)} V ${height - margin.bottom} H ${rec_limit_right}`
        );

        prairie_data = _.isEmpty(prairie_data) ? null : groupRegionData(prairie_data)
        canada_data = _.isEmpty(canada_data) ? null : groupRegionData(canada_data);

        if(info_error_modal) info_error_modal.hide();
    }

    const groupRegionData = (data) => {
        // groups region data into seperate components
        const all_data_point = _.find(data, (d) => d.section === 'All');

        return {
            all_data: all_data_point ? [all_data_point] : [],
            gender_data: _.filter(data, (d) => d.section === 'Males' || d.section === 'Females'),
            age_data: _.filter(data, (d) => d.section === 'Age')
        };
    }
    
    let chevron_height = 10;
    const chevronClick = () => {
        scale = _.find(scales, (scale_option) => {
            return scale_option.key !== scale;
        }).key;
    };
    let info_errors_to_show = {};
    let info_error_modal = null;
    const horizontalGraphShowError = (errors) => {
        info_errors_to_show = errors;
        info_error_modal.show();
    };
</script>

<div class="x-axis-labels" style={`margin-left:${margin.left}px`}>
    <div class="axis-label x-axis-unit">{unit}</div>
    {#if rec_limit}
        <div class="axis-label recommended-limit-text">
            The biomonitoring guidance value for {chemical_substance} concentrations is 
            <b>{rec_limit} {unit}</b>
        </div>
    {/if}
</div>
<div class="horizontal-graph__graph" bind:clientWidth={width} bind:clientHeight={height}>
    <img 
        id="horizontal-graph__scale" 
        src={`${icon_folder}images/chevron-${scale === 'data' ? 'right' : 'left'}-icon.svg`} 
        alt={`chevron`}
        height={chevron_height}
        style={`top:${8}px;right:${-chevron_height - 5}px`}
        on:click={chevronClick}
    />
    <svg {width} height={height}>
        {#each x_scale.ticks(constants.num_axis_ticks) as tick}
            <g>
                <text 
                    class="axis-label" 
                    text-anchor="middle"
                    transform={`translate(${x_scale(tick)} ${axis_font_size + 6})`}
                >{tick}</text>
                <line 
                    class="axis-reference" 
                    x1={0} x2={0} 
                    y1={0} y2={height - margin.top - margin.bottom}
                    transform={`translate(${x_scale(tick)} ${margin.top})`}
                />
            </g>
        {/each}
        {#if min_age_label}
            <text 
                id="age-axis-header" 
                alignment-baseline="middle"
                transform={`translate(0 ${y_scale(min_age_label) - 1.5 * axis_font_size})`}
            >Age</text>
        {/if}
        {#each y_domain as static_val}
            <g transform={`translate(0 ${y_scale(static_val)})`}>
                <text 
                    id={`${static_val}-text`}
                    class={`axis-label${selected_age === static_val ? ' selected' : ''}`} 
                    alignment-baseline="middle" 
                    style="font-size:14px;"
                >{static_val}</text>
                {#if data_errors[static_val].missing_data_strings.length > 0 || data_errors[static_val].unreliable_data_strings.length > 0}
                    <image 
                        class="horizontal-graph__caution-icon"
                        transform={`translate(60 -10)`} 
                        href={`${icon_folder}/images/${data_errors[static_val].missing_data_strings.length > 0 ? 'grey' : 'yellow'}-caution-icon.svg`}
                        on:click={() => {horizontalGraphShowError(data_errors[static_val])}}
                    ></image>
                {/if}
            </g>
        {/each}
        {#if lod_path}
            <path 
                class="lod-path"
                d={lod_path}
            />
        {/if}
        {#if rec_limit_path}
            <path 
                class="rec-limit-path"
                d={rec_limit_path}
            />
        {/if}
        {#if !_.isNull(prairie_data)}
            <g transform={_.isNull(canada_data) ? '' : `translate(0 8)`}>
                <HorizontalGraphPoints
                    dynamic_scale={x_scale}
                    static_scale={y_scale}
                    all_data={prairie_data.all_data}
                    gender_data={prairie_data.gender_data}
                    age_data={prairie_data.age_data}
                    region="prairies"
                />
            </g>
        {/if}
        {#if !_.isNull(canada_data)}
            <g transform={_.isNull(prairie_data) ? '' : `translate(0 -8)`}>
                <HorizontalGraphPoints
                    dynamic_scale={x_scale}
                    static_scale={y_scale}
                    all_data={canada_data.all_data}
                    gender_data={canada_data.gender_data}
                    age_data={canada_data.age_data}
                    region="canada"
                />
            </g>
        {/if}
        {#if lod_path}
            <text 
                class="axis-label"
                text-anchor="middle"
                alignment-baseline="hanging"
                transform={`translate(${lod_left + 5} ${margin.top + (height - margin.top - margin.bottom) / 2}) rotate(-90)`}
            >{`limit of detection${show_lod_num ? ` ${lod}${unit}` : ''}`}</text>
        {/if}
        {#if rec_limit_path}
            <text 
                class="axis-label"
                text-anchor="middle"
                transform={`translate(${rec_limit_right - 5 - axis_font_size} ${margin.top + (height - margin.top - margin.bottom) / 2}) rotate(-90)`}
            >{`biomonitoring guidance value: ${rec_limit}${unit}`}</text>
        {/if}
        {#if source}
            <text 
                class="axis-label"
                text-anchor="middle"
                transform={`translate(${rec_limit_right - 5} ${margin.top + (height - margin.top - margin.bottom) / 2}) rotate(-90)`}
            >{`(${source})`}</text>
        {/if}
    </svg>
    <Modal
        background_dark={false}
        whole_screen={false}
        bind:this={info_error_modal}
    >
        <div id="horizontal-graph__info-error-modal">
            {#if _.get(info_errors_to_show, 'missing_data_strings.length', 0) > 0}
                <div class="horizontal-graph__info-error-modal-header">
                    Missing Data
                </div>
                {#each info_errors_to_show.missing_data_strings as error_string}
                    <div class="horizontal-graph__info-error-modal-content">
                        {error_string}
                    </div>
                {/each}
            {/if}
            {#if _.get(info_errors_to_show, 'unreliable_data_strings.length', 0) > 0}
                <div class="horizontal-graph__info-error-modal-header">
                    Unreliable Data
                </div>
                {#each info_errors_to_show.unreliable_data_strings as error_string}
                    <div class="horizontal-graph__info-error-modal-content">
                        {error_string}
                    </div>
            {/each}
            {/if}
        </div>
    </Modal>
</div>

<style>
    .horizontal-graph__graph {
        position: relative;
        height: 448px;
        width: 100%;
    }
    #horizontal-graph__scale {
        position: absolute;
        cursor: pointer;
    }
    .x-axis-labels {
        display: flex;
        justify-content: space-between;
    }
    .x-axis-unit {
        flex: none;
        padding-right: 64px;
    }
    .recommended-limit-text {
        font-size: 12px;
        text-align: right;
    }
    #age-axis-header {
        fill: #777;
        font-size: 10px;
        text-transform: uppercase;
    }
    .horizontal-graph__caution-icon {
        cursor: pointer;
    }
    #horizontal-graph__info-error-modal {
        margin: 16px 32px;
    }
    .horizontal-graph__info-error-modal-header {
        color: #444;
        font-size: 15px;
        font-weight: 700;
    }
    .horizontal-graph__info-error-modal-header:not(:first-of-type){
        padding-top: 16px;
    }
    .horizontal-graph__info-error-modal-content {
        color: #777;
        font-size: 16px;
        padding-top: 4px;
    }
    @media screen and (min-width: 1199px) {
        .horizontal-graph__graph {
            position: relative;
            width: 610px;
        }
    }
    @media screen and (min-height: 1000px) {
        .horizontal-graph__graph {
            height: 512px;
        }
    }
</style>